import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { motion } from 'framer-motion';
import { EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { login, googleLogin, clearError } from '../../store/slices/authSlice';
import { useGoogleLogin } from '@react-oauth/google';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import axios from 'axios';
import toast from 'react-hot-toast';
import Logo from '../../components/common/Logo';
import EarlyAccessModal from '../../components/auth/EarlyAccessModal';
import { Capacitor } from '@capacitor/core';
import { GoogleAuth } from '@codetrix-studio/capacitor-google-auth';

const schema = yup.object({
  email: yup.string().email('Invalid email').required('Email is required'),
  password: yup.string().min(6, 'Password must be at least 6 characters').required('Password is required'),
});

type LoginFormData = yup.InferType<typeof schema>;

const LoginPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { loading, error } = useAppSelector((state) => state.auth);
  const [searchParams] = useSearchParams();
  const verificationPending = searchParams.get('verified') === 'pending';
  const [resending, setResending] = useState(false);
  const [isEarlyAccessModalOpen, setIsEarlyAccessModalOpen] = useState(false);

  useEffect(() => {
    if (error === 'ERR_NO_EARLY_ACCESS') {
      setIsEarlyAccessModalOpen(true);
      dispatch(clearError());
    }
  }, [error, dispatch]);

  const googleLoginAction = useGoogleLogin({
    onSuccess: async (tokenResponse) => {
      try {
        const result = await dispatch(googleLogin({ access_token: tokenResponse.access_token }));
        if (googleLogin.fulfilled.match(result)) {
          const payload = result.payload as any;
          if (payload.is_new_user || !payload.user.user_type) {
            navigate('/profile/complete');
          } else {
            const redirectUrl = localStorage.getItem('redirect_after_login');
            if (redirectUrl && redirectUrl !== '/login' && redirectUrl !== '/register' && redirectUrl !== '/') {
              localStorage.removeItem('redirect_after_login');
              navigate(redirectUrl);
            } else {
              navigate('/dashboard');
            }
          }
        }
      } catch (error: any) {
        console.error('Web Google login failed', error);
      }
    },
    onError: () => {
      // console.log('Login Failed');
    },
  });

  const handleNativeGoogleLogin = async () => {
    try {
      await GoogleAuth.initialize({
        clientId: '393954976438-gafhoadrsao45md581r6k5nrchkoslqc.apps.googleusercontent.com',
        scopes: ['profile', 'email'],
        grantOfflineAccess: true,
      });
      const user = await GoogleAuth.signIn(); 
      // user.authentication.accessToken or idToken
      const token = user.authentication.idToken || user.authentication.accessToken;
      if (!token) {
        toast.error('Native Google Login token not received. Try again.');
        return;
      }

      // 2. Wrap only API trigger in try/catch to ensure API failure displays toast
      let loginResult;
      try {
        loginResult = await dispatch(googleLogin({ 
          id_token: user.authentication.idToken, 
          access_token: user.authentication.accessToken 
        }));
      } catch (apiError: any) {
        console.error('App Google API login error:', apiError);
        toast.error('App Google Login API failed. Please try again.');
        return;
      }

      // 3. Handle fulfilled state outside API catch layout to prevent False Positives
      if (googleLogin.fulfilled.match(loginResult)) {
        const payload = loginResult.payload as any;
        if (payload.is_new_user || !payload.user.user_type) {
          navigate('/profile/complete');
        } else {
          const redirectUrl = localStorage.getItem('redirect_after_login');
          if (redirectUrl && redirectUrl !== '/login' && redirectUrl !== '/register' && redirectUrl !== '/') {
            localStorage.removeItem('redirect_after_login');
            navigate(redirectUrl);
          } else {
            navigate('/dashboard');
          }
        }
      } else {
        toast.error('App Login failed - Invalid verification.');
      }
    } catch (error: any) {
      if (error && error.type === 'userLoggedOut') return;
      console.error('Native Google login error:', error);
      toast.error(`App Google Login failed: ${JSON.stringify(error)}`, { duration: 6000 });
    }
  };

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(schema),
  });

  const emailValue = watch('email');

  const isUnverifiedError = error ? error.toLowerCase().includes('not verified') : false;

  const onSubmit = async (data: LoginFormData) => {
    const result = await dispatch(login(data));
    if (login.fulfilled.match(result)) {
      const payload = result.payload as any;
      if (!payload.user.user_type) {
         navigate('/profile/complete');
      } else {
        const redirectUrl = localStorage.getItem('redirect_after_login');
        if (redirectUrl && redirectUrl !== '/login' && redirectUrl !== '/register' && redirectUrl !== '/') {
          localStorage.removeItem('redirect_after_login');
          navigate(redirectUrl);
        } else {
          navigate('/dashboard');
        }
      }
    }
  };

  const handleResendVerification = async () => {
    if (!emailValue) {
      toast.error('Please enter your email address');
      return;
    }
    
    setResending(true);
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/auth/resend-verification`, { email: emailValue });
      toast.success('Verification email sent! Please check your inbox.');
      // Button will disappear when error is cleared or user logs in successfully
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to send verification email');
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-white dark:bg-gray-900">
      {/* Left Side - Hero Image */}
      <div className="hidden lg:flex lg:w-1/2 relative overflow-hidden bg-black">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-gray-800 to-black opacity-80 z-10"></div>
        <div className="relative z-20 flex flex-col justify-center items-center text-white p-12 h-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center max-w-lg"
          >
            <img src="/light-logo.png" alt="ClickCollab" className="h-16 mb-4 filter drop-shadow-md mx-auto" />
            <p className="text-2xl text-gray-300 mb-12 font-light">
              The professional network for the visual arts community.
            </p>
          </motion.div>
        </div>
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: 'url("/login-bg-new.png")'
          }}
        ></div>
      </div>
      
      {/* Right Side - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 lg:p-16">
        <motion.div
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.8 }}
          className="w-full max-w-md space-y-8"
        >
          <div className="text-center">
            <div className="flex justify-center mb-2">
              <Link to={Capacitor.isNativePlatform() ? "/login" : "/home"}>
                <Logo className="w-20 h-20 shadow-lg rounded-xl hover:opacity-90 transition-opacity cursor-pointer" />
              </Link>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 dark:text-white tracking-tight">Welcome back</h2>
            <p className="mt-2 text-gray-500 dark:text-gray-400">Sign in to your account to continue</p>
          </div>

          {verificationPending && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-green-800">Registration successful</h3>
                <div className="mt-2 text-sm text-green-700">
                  <p>Please check your email to verify your account before signing in.</p>
                </div>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex flex-col items-start">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-red-800">Login failed</h3>
                  <div className="mt-1 text-sm text-red-700">
                    <p>{error}</p>
                  </div>
                </div>
              </div>
              
              {isUnverifiedError && (
                <div className="mt-3 ml-8">
                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resending}
                    className="text-sm font-medium text-red-800 underline hover:text-red-900 disabled:opacity-50"
                  >
                    {resending ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="space-y-4">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Email Address
                </label>
                <Input
                  {...register('email')}
                  type="email"
                  placeholder="name@example.com"
                  className={errors.email ? 'border-red-500 focus-visible:ring-red-500' : ''}
                />
                {errors.email && (
                  <p className="mt-1.5 text-sm text-red-500">{errors.email.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Input
                    {...register('password')}
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Enter your password"
                    className={errors.password ? 'border-red-500 focus-visible:ring-red-500 pr-10' : 'pr-10'}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  >
                    {showPassword ? (
                      <EyeSlashIcon className="h-5 w-5" />
                    ) : (
                      <EyeIcon className="h-5 w-5" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1.5 text-sm text-red-500">{errors.password.message}</p>
                )}
              </div>
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <input
                  id="remember-me"
                  name="remember-me"
                  type="checkbox"
                  className="h-4 w-4 text-black focus:ring-black border-gray-300 rounded transition-colors"
                />
                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-600 dark:text-gray-400">
                  Remember me
                </label>
              </div>

              <Link
                to="/forgot-password"
                className="text-sm font-medium text-black dark:text-white hover:text-gray-700 dark:hover:text-gray-300 hover:underline"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              size="lg"
              disabled={loading}
              className="w-full bg-black dark:bg-white hover:bg-gray-800 dark:hover:bg-gray-200 text-white dark:text-black font-bold py-6 rounded-xl shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-0.5"
            >
              {loading ? 'Signing in...' : 'Sign In'}
            </Button>

            <div className="relative my-8">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-4 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400 font-medium">Or continue with</span>
              </div>
            </div>

            <Button
              type="button"
              variant="outline"
              size="lg"
              className="w-full py-6 rounded-xl border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600 transition-all font-medium text-gray-700 dark:text-gray-200"
              onClick={() => Capacitor.isNativePlatform() ? handleNativeGoogleLogin() : googleLoginAction()}
            >
              <img
                src="/google-icon.ico"
                alt="Google"
                className="w-5 h-5 mr-3"
              />
              Sign in with Google
            </Button>
          </form>

          <p className="text-center text-sm text-gray-600 dark:text-gray-400">
            Don't have an account?{' '}
            <Link to="/register" className="font-bold text-black dark:text-white hover:underline">
              Sign up for free
            </Link>
          </p>
        </motion.div>
      </div>
      <EarlyAccessModal 
        isOpen={isEarlyAccessModalOpen} 
        onClose={() => setIsEarlyAccessModalOpen(false)} 
      />
    </div>
  );
};

export default LoginPage;
